import { RowClassParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Notification,
  notificationsAtom,
  NotificationsState,
} from '~/atoms/workflowsSimpleAtom';
import { agTheme } from '~/components/ui/tables/ag/agTheme';
import { getColDefsWorkflowTable, Workflow } from './colDefsWorkflowTable';
import { PriorityCellRenderer } from './PriorityCellRenderer';
import { SelectCellRenderer } from './SelectCellRenderer';
import { StatusCellRenderer } from './StatusCellRenderer';

export type WorkflowTableProps = {
  selectedWorkflow: Workflow | null;
  onWorkflowSelect: (notification: Notification) => void;
  quickFilterText: string;
};

export const WorkflowTable = ({
  selectedWorkflow,
  onWorkflowSelect,
  quickFilterText,
}: WorkflowTableProps) => {
  const gridRef = useRef<AgGridReact<Notification>>(null);
  const [gridReady, setGridReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use useAtom directly to get the setters
  const [notifications] = useAtom(notificationsAtom);
  const [hasRedrawnColumns] = useState(false);
  const notificationsRef = useRef<NotificationsState>(notifications);

  useEffect(() => {
    console.log('[WorkflowTable] notifications state changed:', {
      length: notifications.notifications.length,
      notificationIds: notifications.notifications
        .map((n) => n.workflow_id)
        .join(', '),
    });

    // Update the ref to point to the latest notifications state
    notificationsRef.current = notifications;
  }, [notifications]);

  // --- Grid updates / initial data loading --- //
  useEffect(() => {
    if (gridRef.current?.api && gridReady) {
      const api = gridRef.current.api;

      if (notifications.notifications.length >= 0) {
        const existingIds = new Set<string>();
        api.forEachNode((node) => {
          if (node.data?.workflow_id) {
            existingIds.add(node.data.workflow_id);
          }
        });

        const toUpdate: Notification[] = [];
        const toAdd: Notification[] = [];

        notifications.notifications.forEach((notification) => {
          if (existingIds.has(notification.workflow_id)) {
            toUpdate.push(notification);
          } else {
            toAdd.push(notification);
          }
        });

        api.applyTransaction({
          update: toUpdate,
          add: toAdd,
        });
      }
    }
  }, [notifications.notifications, hasRedrawnColumns, gridReady]);

  const previousPropsRef = useRef({ onWorkflowSelect, quickFilterText });
  useEffect(() => {
    const propsChanged = {
      onWorkflowSelect:
        previousPropsRef.current.onWorkflowSelect !== onWorkflowSelect,
      quickFilterText:
        previousPropsRef.current.quickFilterText !== quickFilterText,
    };

    if (propsChanged.onWorkflowSelect || propsChanged.quickFilterText) {
      console.log('[WorkflowTable] Props changed:', {
        propsChanged,
        previous: previousPropsRef.current,
        current: { onWorkflowSelect, quickFilterText },
      });

      previousPropsRef.current = { onWorkflowSelect, quickFilterText };
    }
  }, [onWorkflowSelect, quickFilterText]);

  const rowClassRules = useMemo(() => {
    return {
      'workflow-row-selected': (params: RowClassParams<Notification>) => {
        return Boolean(
          selectedWorkflow &&
            params.data &&
            params.data.workflow_id === selectedWorkflow.workflow_id,
        );
      },
    };
  }, [selectedWorkflow]);

  const columnDefs = useMemo(
    () => getColDefsWorkflowTable({ onWorkflowSelect }),
    [onWorkflowSelect],
  );

  const components = useMemo(() => {
    return {
      statusCellRenderer: StatusCellRenderer,
      priorityCellRenderer: PriorityCellRenderer,
      selectCellRenderer: SelectCellRenderer,
    };
  }, []);

  const onGridReady = useCallback(() => {
    console.log('[Grid] - Grid is ready');
    if (gridRef.current?.api) {
      const api = gridRef.current.api;

      // Set initial data
      api.applyTransaction({
        add: notifications.notifications,
      });

      api.sizeColumnsToFit();
      setGridReady(true);
      // Set visibility to visible as a backup
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.style.visibility = 'visible';
        }
      }, 10);
    }
  }, [notifications.notifications]);

  const onFirstDataRendered = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.sizeColumnsToFit();
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.style.visibility = 'visible';
        }
      }, 10);
    }
  }, []);

  return (
    <div
      className="ag-theme-quartz"
      style={{ height: '100%', visibility: 'hidden', width: '100%' }}
      ref={containerRef}
    >
      <AgGridReact
        {...(typeof undefined !== 'undefined' && { key: undefined })}
        ref={gridRef}
        columnDefs={columnDefs}
        getRowId={(params) => params.data.workflow_id}
        theme={agTheme}
        loadThemeGoogleFonts={false}
        rowClassRules={rowClassRules}
        components={components}
        onGridReady={onGridReady}
        onFirstDataRendered={onFirstDataRendered}
        quickFilterText={quickFilterText}
        tooltipShowDelay={0}
        pagination={true}
        paginationAutoPageSize={true}
        domLayout="normal"
        animateRows={true}
        rowModelType="clientSide"
        suppressScrollOnNewData={true}
        rowBuffer={0}
        autoSizeStrategy={{
          type: 'fitGridWidth',
          defaultMinWidth: 100,
        }}
      />
    </div>
  );
};

export default WorkflowTable;
